/**
 * projectMetrics.js — SINGLE SOURCE OF TRUTH for verified project results.
 *
 * Every metric that appears on a public case study / service page must live
 * here first. Nothing renders publicly unless `approvedForPublicDisplay === true`.
 *
 * Never invent numbers. If a value is uncertain, keep it here with the
 * `approvedForPublicDisplay: false` flag so it stays private until the
 * project owner confirms it.
 *
 * Fields:
 *   id                        stable identifier
 *   project                   caseStudy slug the metric belongs to
 *   kind                      "outcome"    — an operational/performance result
 *                             "capability" — a verified product trait / config
 *   label                     short public label (what the metric measures)
 *   before                    prior state (optional)
 *   after                     new state (optional)
 *   value                     single-value form (used when before/after not applicable)
 *   qualifier                 e.g. "approximately", "pilot", "internal test"
 *   context                   plain-English explanation of what the number refers to
 *   relevantFor               [] of Phase 3.1 service slugs where this metric is
 *                             genuinely evidence of the capability (used by ProofStrip
 *                             to prevent statistic-spam across services)
 *   source                    internal note (NEVER rendered publicly)
 *   lastUpdated               ISO date the metric was last owner-confirmed
 *   approvedForPublicDisplay  gate — only `true` shows on public pages
 */

export const PROJECT_METRICS = [
  // ---------------------- UniCRM.ai --------------------------------
  {
    id: "unicrm-document-vetting-time",
    project: "unicrm-ai",
    kind: "outcome",
    label: "Document vetting time",
    before: "~30 minutes",
    after: "~10 seconds",
    qualifier: "approximately · UniCRM.ai pilot implementation",
    context:
      "Time for a counsellor to vet a single student's document pack (passport, transcripts, English certificates) — manual review vs. AI-assisted vetting inside UniCRM.ai.",
    relevantFor: ["ai-development", "ai-agent-development", "edtech-development"],
    source: "Owner-verified UniCRM.ai pilot figure supplied 2026-02.",
    lastUpdated: "2026-02-01",
    approvedForPublicDisplay: true,
  },
  {
    id: "unicrm-counsellor-load-ratio",
    project: "unicrm-ai",
    kind: "outcome",
    label: "Counsellor : student load ratio",
    before: "~174 : 1",
    after: "~14 : 1",
    qualifier: "approximately · UniCRM.ai pilot implementation",
    context:
      "Active students each counsellor could realistically own before automation vs. after routing, doc-vetting and AI copilots inside UniCRM.ai.",
    relevantFor: ["crm-development", "edtech-development"],
    source: "Owner-verified UniCRM.ai pilot figure supplied 2026-02.",
    lastUpdated: "2026-02-01",
    approvedForPublicDisplay: true,
  },
  {
    id: "unicrm-pilot-leads-handled",
    project: "unicrm-ai",
    kind: "outcome",
    label: "Pilot leads handled",
    value: "~19,000",
    qualifier: "approximately · UniCRM.ai pilot with 2 operational staff",
    context:
      "Total inbound leads processed by 2 operational staff during the UniCRM.ai pilot workspace using WhatsApp automation and AI-assisted routing.",
    relevantFor: ["crm-development", "saas-development", "edtech-development"],
    source: "Owner-verified UniCRM.ai pilot figure supplied 2026-02.",
    lastUpdated: "2026-02-01",
    approvedForPublicDisplay: true,
  },
  {
    id: "unicrm-ai-agents-per-workspace",
    project: "unicrm-ai",
    kind: "capability",
    label: "AI agents per workspace",
    value: "3",
    qualifier: "product configuration",
    context:
      "Specialised agents shipped in each UniCRM.ai workspace: Counsellor, Admissions and Customer Care.",
    relevantFor: ["ai-development", "ai-agent-development", "saas-development"],
    source: "Verified in productConfigs.js (unicrm-ai.hero.stats + features).",
    lastUpdated: "2026-02-01",
    approvedForPublicDisplay: true,
  },
  {
    id: "unicrm-whatsapp-availability",
    project: "unicrm-ai",
    kind: "capability",
    label: "WhatsApp automation availability",
    value: "24/7",
    qualifier: "product configuration",
    context:
      "UniCRM.ai's WhatsApp inbox runs continuously with AI-suggested replies and automated lead routing.",
    relevantFor: ["ai-agent-development", "crm-development"],
    source: "Verified in productConfigs.js (unicrm-ai.hero.stats + features).",
    lastUpdated: "2026-02-01",
    approvedForPublicDisplay: true,
  },
  // ---------------------- UniCRM.ai — HELD (owner reconfirmation) ---
  {
    id: "unicrm-applications-tracked",
    project: "unicrm-ai",
    label: "Applications tracked",
    value: "50K+",
    qualifier: "product-page figure · pending owner reconfirmation",
    context: "Aggregate applications tracked across UniCRM.ai workspaces.",
    source: "productConfigs.js hero stat. Held pending 2026 reconfirmation.",
    lastUpdated: "2026-02-01",
    approvedForPublicDisplay: false,
  },
  {
    id: "unicrm-countries-supported",
    project: "unicrm-ai",
    label: "Countries supported",
    value: "60+",
    qualifier: "product-page figure · pending owner reconfirmation",
    context: "Countries with active workspaces or supported programmes.",
    source: "productConfigs.js hero stat. Held pending 2026 reconfirmation.",
    lastUpdated: "2026-02-01",
    approvedForPublicDisplay: false,
  },

  // ---------------------- TeachingSoftware.ai ----------------------
  // No metrics are publicly approved yet. Any value inside the product page
  // (e.g. "teachers using it", "lessons planned") must be reviewed by the
  // owner before we render it. Leaving the array empty is intentional and
  // the case study will simply hide its metrics section.
];

// ---------------- helpers --------------------------------------------------
export function getMetricsForProject(slug, { publicOnly = true, kind = null } = {}) {
  return PROJECT_METRICS.filter(
    (m) =>
      m.project === slug &&
      (!publicOnly || m.approvedForPublicDisplay === true) &&
      (!kind || m.kind === kind),
  );
}

/**
 * Metrics that are (a) publicly approved and (b) relevant to the given
 * Phase 3.1 service slug — used by ProofStrip to prevent statistic spam.
 * Optional `kind` filter keeps outcomes/capabilities separable.
 */
export function getMetricsForService(serviceSlug, { kind = null } = {}) {
  return PROJECT_METRICS.filter(
    (m) =>
      m.approvedForPublicDisplay === true &&
      Array.isArray(m.relevantFor) &&
      m.relevantFor.includes(serviceSlug) &&
      (!kind || m.kind === kind),
  );
}

export function getMetricById(id) {
  return PROJECT_METRICS.find((m) => m.id === id) || null;
}
